import { WebAppState } from '../realtime';
import type { CallerVideoConnectionState, CameraState, RecordingState } from './videoSignalling';

export type VideoCallState = {
  callerConnectionState: CallerVideoConnectionState;
  recordingState: RecordingState;
  cameraState: CameraState;
  webAppState: WebAppState;
  showRecording: boolean;
  activeProducerId: string | null;
  consumedProducerId: string | null;
  transportRecoveryInProgress: boolean;
};

export const INITIAL_VIDEO_CALL_STATE: VideoCallState = {
  callerConnectionState: 'Connecting',
  recordingState: 'NotReady',
  cameraState: 'Enabled',
  webAppState: WebAppState.None,
  showRecording: false,
  activeProducerId: null,
  consumedProducerId: null,
  transportRecoveryInProgress: false,
};

export type VideoCallEvent =
  | {
      type: 'STATES_LOADED';
      callerConnectionState: CallerVideoConnectionState;
      recordingState: RecordingState;
      cameraState: CameraState;
      webAppState: WebAppState;
    }
  | { type: 'JOIN_ROOM_SUCCEEDED'; activeProducerId: string | null }
  | { type: 'NEW_PRODUCER_AVAILABLE'; producerId: string }
  | { type: 'PRODUCER_STOPPED'; stoppedProducerId: string }
  | { type: 'CALLER_VIDEO_CONNECTION_STATE_CHANGED'; state: CallerVideoConnectionState }
  | { type: 'RECORDING_STATE_CHANGED'; state: RecordingState }
  | { type: 'CAMERA_STATE_CHANGED'; state: CameraState }
  | { type: 'WEB_APP_STATE_CHANGED'; state: WebAppState }
  | { type: 'CONSUME_SUCCEEDED'; producerId: string }
  | { type: 'CONSUME_ATTEMPT_FAILED' }
  | { type: 'TRANSPORT_RECOVERY_STARTED' }
  | { type: 'TRANSPORT_RECOVERY_FINISHED' }
  | { type: 'REWIND_TO_RECORDING' }
  | { type: 'RESUME_LIVE_STREAM' };

export function isStaleProducerStop(state: VideoCallState, stoppedProducerId: string): boolean {
  return state.consumedProducerId !== null && state.consumedProducerId !== stoppedProducerId;
}

export function videoCallReducer(state: VideoCallState, event: VideoCallEvent): VideoCallState {
  switch (event.type) {
    case 'STATES_LOADED':
      return {
        ...state,
        callerConnectionState: event.callerConnectionState,
        recordingState: event.recordingState,
        cameraState: event.cameraState,
        webAppState: event.webAppState,
      };

    case 'JOIN_ROOM_SUCCEEDED':
      return { ...state, activeProducerId: event.activeProducerId };

    case 'NEW_PRODUCER_AVAILABLE':
      return { ...state, activeProducerId: event.producerId };

    case 'PRODUCER_STOPPED':
      if (isStaleProducerStop(state, event.stoppedProducerId)) {
        return state;
      }
      return {
        ...state,
        activeProducerId: null,
        consumedProducerId: null,
        callerConnectionState: 'Disconnected',
      };

    case 'CALLER_VIDEO_CONNECTION_STATE_CHANGED':
      return { ...state, callerConnectionState: event.state };

    case 'RECORDING_STATE_CHANGED':
      return { ...state, recordingState: event.state };

    case 'CAMERA_STATE_CHANGED':
      return { ...state, cameraState: event.state };

    case 'WEB_APP_STATE_CHANGED':
      return { ...state, webAppState: event.state };

    case 'CONSUME_SUCCEEDED':
      return { ...state, consumedProducerId: event.producerId };

    case 'CONSUME_ATTEMPT_FAILED':
      return { ...state, consumedProducerId: null };

    case 'TRANSPORT_RECOVERY_STARTED':
      if (state.transportRecoveryInProgress) {
        return state;
      }
      return { ...state, transportRecoveryInProgress: true, consumedProducerId: null };

    case 'TRANSPORT_RECOVERY_FINISHED':
      return { ...state, transportRecoveryInProgress: false };

    case 'REWIND_TO_RECORDING':
      return { ...state, showRecording: true };

    case 'RESUME_LIVE_STREAM':
      return { ...state, showRecording: false };

  }
}

export type DisplayPanelMode =
  | 'RecordingPlayback'
  | 'FatalError'
  | 'Disconnected'
  | 'Connecting'
  | 'CameraTurnedOff'
  | 'AppleEedInstructions'
  | 'LiveVideo'
  | 'WebAppState';

export function isVideoErrorState(webAppState: WebAppState): boolean {
  return (
    webAppState === WebAppState.CameraPermissionRejected ||
    webAppState === WebAppState.UnsupportedBrowser ||
    webAppState === WebAppState.VideoTransmissionFailed
  );
}

export function getDisplayPanelMode(state: VideoCallState, isAppleEedVideo: boolean): DisplayPanelMode {
  if (state.showRecording) return 'RecordingPlayback';

  if (isVideoErrorState(state.webAppState)) return 'FatalError';

  if (state.callerConnectionState === 'Disconnected') return 'Disconnected';

  if (
    state.callerConnectionState === 'Connected' &&
    state.cameraState !== 'Enabled'
  ) {
    return 'CameraTurnedOff';
  }

  if (state.callerConnectionState === 'Connecting') return 'Connecting';

  if (state.callerConnectionState === 'NotConnected') {
    return isAppleEedVideo ? 'AppleEedInstructions' : 'WebAppState';
  }

  if (state.callerConnectionState === 'Connected') return 'LiveVideo';

  return 'WebAppState';
}

export function getWebAppStateIcon(): string {
  return 'Images/Incident/video-loading.svg';
}

export function getWebAppStateTextKey(webAppState: WebAppState): string {
  switch (webAppState) {
    case WebAppState.None:
    case WebAppState.WebAppSmsSent:
      return 'WebAppState_WaitingForLinkOpen';
    case WebAppState.AppOpened:
      return 'WebAppState_AppOpened';
    case WebAppState.CameraPermissionRequested:
      return 'WebAppState_CameraPermissionRequested';
    case WebAppState.CameraPermissionGranted:
      return 'WebAppState_CameraPermissionGranted';
    case WebAppState.CameraPermissionRejected:
      return 'WebAppState_CameraPermissionRejected';
    case WebAppState.UnsupportedBrowser:
      return 'WebAppState_UnsupportedBrowser';
    case WebAppState.StartingVideoTransmission:
      return 'WebAppState_StartingVideoTransmission';
    case WebAppState.VideoTransmissionActive:
      return 'WebAppState_VideoTransmissionActive';
    case WebAppState.VideoTransmissionFailed:
      return 'WebAppState_VideoTransmissionFailed';
    default:
      return 'WebAppState_WaitingForLinkOpen';
  }
}

export function getWebAppStateDescriptionKey(webAppState: WebAppState): string | null {
  switch (webAppState) {
    case WebAppState.None:
    case WebAppState.WebAppSmsSent:
      return 'WebAppState_WaitingForLinkOpen_Description';
    case WebAppState.CameraPermissionRejected:
      return 'WebAppState_CameraPermissionRejected_Description';
    case WebAppState.UnsupportedBrowser:
      return 'WebAppState_UnsupportedBrowser_Description';
    case WebAppState.StartingVideoTransmission:
      return 'WebAppState_StartingVideoTransmission_Description';
    case WebAppState.VideoTransmissionFailed:
      return 'WebAppState_VideoTransmissionFailed_Description';
    default:
      return null;
  }
}

export function isRewindDisabled(state: VideoCallState): boolean {
  return state.recordingState === 'NotReady';
}

export function canSwapCameraOrTakeSnapshot(state: VideoCallState): boolean {
  return (
    state.callerConnectionState === 'Connected' &&
    state.cameraState === 'Enabled'
  );
}

export function showLiveVideo(
  showRecording: boolean,
  connectionState: CallerVideoConnectionState | null,
  cameraState: CameraState,
): boolean {
  return (
    !showRecording &&
    connectionState === 'Connected' &&
    cameraState === 'Enabled'
  );
}

export const CALLER_VIDEO_CONNECTION_STATE_NAMES: Record<CallerVideoConnectionState, string> = {
  NotConnected: 'NotConnected',
  Connecting: 'Connecting',
  Connected: 'Connected',
  Disconnected: 'Disconnected',
};

export const RECORDING_STATE_NAMES: Record<RecordingState, string> = {
  NotReady: 'NotReady',
  Ready: 'Ready',
};

export const CAMERA_STATE_NAMES: Record<CameraState, string> = {
  Enabled: 'Enabled',
  Disabled: 'Disabled',
};
